/**
 * GlobalFunctions — utilidades globales de la aplicación.
 *
 * Contiene `ApiRequest`, el wrapper centralizado sobre axios que:
 * - Adjunta automáticamente el token de autenticación (ltkn) y store_id.
 * - Acepta un callback opcional `onError` para manejo de errores por componente.
 * - Si `onError` no se provee, llama a `setResponse` con un objeto de error
 *   estándar `{ status: 'error', message }` para no dejar al componente colgado.
 * - Tiene timeout de 30 segundos para evitar peticiones infinitas.
 * - Solo loguea en consola en modo desarrollo.
 * - Deja que axios auto-detecte el Content-Type en FormData (incluye el boundary).
 */

import axios, { Method } from 'axios';
import { ProjectConfig } from 'Global';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Parámetros de configuración de una petición API.
 */
export interface ApiRequestParams {
  /** Ruta relativa del endpoint (ej. '/users/details') */
  url: string;
  /** Método HTTP */
  method: Method | string;
  /** Headers adicionales (opcional) */
  headers?: Record<string, string>;
  /** Cuerpo de la petición (objeto JSON o FormData) */
  body?: Record<string, any> | FormData | null;
  /** Parámetros de query string */
  query?: Record<string, any> | null;
  /** true si el body es FormData (subida de archivos) */
  formData?: boolean;
  /** Timeout en ms para esta petición (por defecto 30000) */
  timeout?: number;
  /**
   * Callback que recibe la respuesta exitosa de la API.
   * También se llama con `{ status: 'error', message }` si no se provee onError.
   */
  setResponse: (data: any) => void;
  /**
   * Callback opcional para manejar errores de red o de la API.
   * Si no se provee, los errores se delegan a setResponse con status='error'.
   */
  onError?: (message: string) => void;
}

// ---------------------------------------------------------------------------
// ApiRequest
// ---------------------------------------------------------------------------

/**
 * Realiza una petición HTTP a la API del proyecto.
 *
 * Adjunta automáticamente el token de sesión (`ltkn`) y el identificador de
 * tienda (`store_id`) desde localStorage si están disponibles.
 *
 * @param params - Configuración de la petición (ver ApiRequestParams)
 * @returns Promise que resuelve cuando la petición finaliza
 *
 * @example
 * await ApiRequest({
 *   url: '/users/details',
 *   method: 'GET',
 *   setResponse: (data) => setUser(data),
 *   onError: (msg) => enqueueSnackbar(msg, { variant: 'error' }),
 * });
 */
export const ApiRequest = async (params: ApiRequestParams): Promise<void> => {
  // -- Auth headers ----------------------------------------------------------
  const authHeaders: Record<string, string> = {};
  const ltkn = localStorage.getItem('ltkn');
  const store_id = localStorage.getItem('store_id');
  if (ltkn) authHeaders['ltkn'] = ltkn;
  if (store_id) authHeaders['store_id'] = store_id;

  // -- Destructure params ----------------------------------------------------
  const {
    url,
    method,
    headers = {},
    body = null,
    query = null,
    formData = false,
    timeout = 30_000,
    setResponse,
    onError,
  } = params;

  // -- Build headers ---------------------------------------------------------
  // Para FormData NO se setea Content-Type: axios lo setea automáticamente
  // e incluye el boundary necesario para multipart.
  const mergedHeaders: Record<string, string> = { ...authHeaders, ...headers };

  // -- Build data ------------------------------------------------------------
  let data: any = null;
  if (formData && body instanceof FormData) {
    data = body;
  } else {
    data = body ?? null;
  }

  // -- Execute ---------------------------------------------------------------
  try {
    const response = await axios.request({
      url: ProjectConfig.api_url + url,
      method: method as Method,
      headers: mergedHeaders,
      params: query ?? undefined,
      data,
      timeout,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${method.toUpperCase()} ${url}`, response.data);
    }

    setResponse(response.data);
  } catch (err: any) {
    // Extraer el mensaje más descriptivo posible
    const message: string =
      err?.response?.data?.message ||
      err?.message ||
      'Error de conexión. Verifica tu internet e intenta de nuevo.';

    if (process.env.NODE_ENV === 'development') {
      console.error(`[API Error] ${method.toUpperCase()} ${url}:`, err);
    }

    if (onError) {
      onError(message);
    } else {
      // Fallback: notificar al componente con un objeto de error estándar
      // para que no quede esperando respuesta indefinidamente.
      setResponse({ status: 'error', message });
    }
  }
};

/**
 * Normaliza un número de teléfono/WhatsApp chileno a 9 dígitos limpios,
 * sin importar cómo lo haya escrito el usuario (con +56, espacios, guiones,
 * 0 inicial, etc.). Devuelve solo los últimos 9 dígitos encontrados.
 *
 * @example normalizePhoneCL('+56 9 1234 5678') // '912345678'
 * @example normalizePhoneCL('912345678')       // '912345678'
 */
export const normalizePhoneCL = (raw: string): string => {
  const digits = (raw || '').replace(/\D/g, '');
  return digits.length > 9 ? digits.slice(-9) : digits;
};

/**
 * Valida un RUT chileno (con o sin puntos/guión) verificando el dígito verificador.
 * @example isValidRutCL('12.345.678-5')
 */
export const isValidRutCL = (rut: string): boolean => {
  if (!rut) return false;
  const clean = String(rut).replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const mod = 11 - (sum % 11);
  const expectedDv = mod === 11 ? '0' : mod === 10 ? 'K' : String(mod);
  return dv === expectedDv;
};
